import React, { useState } from 'react'
import { Checkbox, Col, Divider, Form, Modal, notification, Row } from 'antd'
import { Formik } from 'formik'
import { useTranslation } from 'react-i18next'
import Compressor from 'compressorjs'
import { UploadChangeParam } from 'antd/lib/upload/interface'
import { AddImage, Empty } from 'src/components/Ant'
import {
  DatePicker,
  Input,
  InputCurrency,
  InputNumber,
  Select,
  SelectResource
} from 'src/components/Formik'
import { Id, RequestTicket, RequestTicketReceiptImage } from 'src/types'
import { ReceiptImageFileTypeAvailable } from 'src/core/forms/receipt'
import ThumbnailPreview from 'src/views/Requests/components/Receipts/components/ThumbnailPreview'
import { isTenant559042084949 } from 'src/core/utils/isTenant559042084949'
import * as CSS from './styles'
import { getValidationSchema } from './validate-shema'
import getInitialValues from './initial-values'

type ModalTicketRenderProps = {
  formikProps: any
  handleSubmit: (values: any) => void
  ticket: RequestTicket
  organization_id: Id
  setPreviewImage: any
}

type ModalTicketProps = {
  handleSubmit: (values: any) => void
  organization_id: Id
  ticket: RequestTicket
  toggle: () => void
}

type HandleAmountExchangeQuantityProps = {
  formikProps: any
}

function HandleAmountExchangeQuantity(
  props: HandleAmountExchangeQuantityProps
) {
  const { service, currency } =
    props.formikProps.values.request_ticket_receipts?.[0] ?? {}

  if (service?.service_type !== 'QUANTITY') {
    return (
      <>
        <Col span={6}>
          <InputCurrency
            dataTestId="receipt-amount"
            disabled
            formikProps={props.formikProps}
            label="web.services.advances.form.amount.title"
            name="request_ticket_receipts.0.amount"
            placeholder="web.services.advances.form.amount.placeholder"
          />
        </Col>
        <Col span={6}>
          <InputCurrency
            dataTestId="receipt-exchange-rate"
            disabled={currency?.code === 'BRL'}
            formikProps={props.formikProps}
            label="web.services.advances.form.exchange-rate.title"
            name="request_ticket_receipts.0.exchange_rate"
            placeholder="web.services.advances.form.exchange-rate.placeholder"
            precision={4}
          />
        </Col>
      </>
    )
  }
  return (
    <Col span={6}>
      <InputNumber
        dataTestId="receipt-quantity"
        disabled
        formikProps={props.formikProps}
        label="web.receipt.form.quantity.title"
        min={0}
        name="request_ticket_receipts.0.quantity"
      />
    </Col>
  )
}

function getBase64(file: File | Blob): Promise<string | ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result)
    reader.onerror = error => reject(error)
  })
}

const numberSizeForUpload = 4
const uploadFilesAccept =
  Object.values(ReceiptImageFileTypeAvailable).join(', ') || '.jpg'

function ModalTicketRender(props: ModalTicketRenderProps) {
  const { t } = useTranslation()

  if (!props.ticket) {
    return <Empty />
  }

  const userList = props.ticket.request_ticket_user_data
    .map(user_data => user_data.traveler)
    .map(traveler => ({
      gender: traveler.gender,
      label: traveler.name,
      value: `${traveler.user_id}`
    }))

  const images: RequestTicketReceiptImage[] =
    props.formikProps.values.request_ticket_receipts[0].request_ticket_receipt_images.filter(
      (item: RequestTicketReceiptImage) => item.active
    )
  return (
    <>
      <Row gutter={24}>
        <Col span={9}>
          <Select
            dataTestId="owner-list"
            disabled
            formikProps={props.formikProps}
            label="web.receipt.form.owner"
            name="request_ticket_user_data.0.traveler.user_id"
            options={userList}
          />
        </Col>
        <Col span={8}>
          <SelectResource
            formikProps={props.formikProps}
            keyLabel="name"
            keyValue="service_id"
            label="web.receipt.form.service.title"
            name="request_ticket_receipts.0.service_id"
            organization_id={props.organization_id}
            placeholder="web.receipt.form.service.placeholder"
            resource="services"
          />
        </Col>
        <Col span={7}>
          <DatePicker
            disabled
            formikProps={props.formikProps}
            label="web.receipt.form.date.title"
            name="request_ticket_receipts.0.date"
            placeholder="web.receipt.form.date.placeholder"
          />
        </Col>
      </Row>
      <Row gutter={24}>
        <Col span={6}>
          <SelectResource
            formikProps={props.formikProps}
            keyLabel="name"
            keyValue="reference_id"
            label="web.receipt.form.reference.title"
            name="request_ticket_receipts.0.reference_id"
            organization_id={props.organization_id}
            placeholder="web.receipt.form.reference.placeholder"
            resource="references"
          />
        </Col>
        <Col span={6}>
          <SelectResource
            formikProps={props.formikProps}
            keyLabel="code"
            keyValue="currency_id"
            label="web.services.advances.form.currency.title"
            name="request_ticket_receipts.0.currency_id"
            organization_id={props.organization_id}
            placeholder="web.services.advances.form.currency.placeholder"
            resource="currencies"
          />
        </Col>
        <HandleAmountExchangeQuantity formikProps={props.formikProps} />
      </Row>
      <Row gutter={24}>
        <Col span={6}>
          <SelectResource
            disabled
            formikProps={props.formikProps}
            keyLabel="name"
            keyValue="payment_resource_id"
            label="web.receipt.form.payment-resource.title"
            name="request_ticket_receipts.0.payment_resource_id"
            organization_id={props.organization_id}
            placeholder="web.receipt.form.payment-resource.placeholder"
            resource="payment_resources"
          />
          {t('web.table.hotel.refundable')}{' '}
          <Checkbox
            disabled
            checked={
              props.formikProps.values.request_ticket_receipts?.[0]
                ?.payment_resource?.refundable ?? false
            }
          />
        </Col>
        <Col span={6}>
          <Input
            data-test-id="receipt-invoice"
            formikProps={props.formikProps}
            label="web.receipt.form.invoice.title"
            name="request_ticket_receipts.0.invoice"
            placeholder="web.receipt.form.invoice.placeholder"
            required={false}
          />
        </Col>
        <Col span={12}>
          <Input
            data-test-id="receipt-justification"
            formikProps={props.formikProps}
            label="web.receipt.form.justification.title"
            name="request_ticket_receipts.0.justification"
            placeholder="web.receipt.form.justification.placeholder"
          />
        </Col>
      </Row>
      <Divider />
      <Row>
        <Col span={12}>
          <CSS.Upload
            accept={uploadFilesAccept}
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            customRequest={() => {}}
            fileList={images.map(item => ({
              uid: (
                item.request_ticket_receipt_image_id ||
                item.tmp_id ||
                0
              ).toString(),
              name: (
                item.request_ticket_receipt_image_id ||
                item.tmp_id ||
                0
              ).toString(),
              size: 0,
              url: item.image_url,
              type: 'done'
            }))}
            listType="picture-card"
            onChange={async ({ file }: UploadChangeParam) => {
              if (!file) {
                return
              }

              const isFileTypeAllowed = uploadFilesAccept.includes(file.type)

              if (!isFileTypeAllowed && file.status !== 'removed') {
                notification.warning({
                  duration: 10,
                  message: t('common.receipt.warning.unsupported-format', {
                    value: uploadFilesAccept
                      .replace(/(image|application)\//g, '')
                      .toUpperCase()
                  }),
                  placement: 'bottomRight'
                })
                return
              }

              if (file.status === 'removed') {
                if (file.uid.indexOf('.') > -1) {
                  props.formikProps.setFieldValue(
                    'request_ticket_receipts.0.request_ticket_receipt_images',
                    props.formikProps.values.request_ticket_receipts[0].request_ticket_receipt_images.filter(
                      (item: RequestTicketReceiptImage) =>
                        +item.tmp_id !== +file.uid
                    )
                  )
                  return
                }

                props.formikProps.setFieldValue(
                  'request_ticket_receipts.0.request_ticket_receipt_images',
                  props.formikProps.values.request_ticket_receipts[0].request_ticket_receipt_images.map(
                    (item: RequestTicketReceiptImage) => ({
                      ...item,
                      active:
                        +item.request_ticket_receipt_image_id === +file.uid
                          ? false
                          : item.active
                    })
                  )
                )
                return
              }

              if (file.type === 'application/pdf') {
                props.formikProps.setFieldValue(
                  'request_ticket_receipts.0.request_ticket_receipt_images',
                  [
                    ...props.formikProps.values.request_ticket_receipts[0]
                      .request_ticket_receipt_images,
                    {
                      active: true,
                      image_url: await getBase64(file.originFileObj),
                      request_ticket_receipt_image_id: '',
                      request_ticket_receipt_image_number: '',
                      tmp_id: Math.random()
                    }
                  ]
                )
              } else {
                new Compressor(file.originFileObj, {
                  mimeType: 'image/jpeg',
                  checkOrientation: false,
                  maxWidth: 1080,
                  maxHeight: 1080,
                  quality: 0.6,
                  async success(result) {
                    props.formikProps.setFieldValue(
                      'request_ticket_receipts.0.request_ticket_receipt_images',
                      [
                        ...props.formikProps.values.request_ticket_receipts[0]
                          .request_ticket_receipt_images,
                        {
                          active: true,
                          image_url: await getBase64(result),
                          request_ticket_receipt_image_id: '',
                          request_ticket_receipt_image_number: '',
                          tmp_id: Math.random()
                        }
                      ]
                    )
                  },
                  error(err) {
                    notification.warning({
                      duration: 10,
                      message: 'web.receipt.form.images.not-attached',
                      placement: 'bottomRight'
                    })
                  }
                })
              }
            }}
            onPreview={file => props.setPreviewImage(file.url)}
          >
            {images.length < numberSizeForUpload && <AddImage />}
          </CSS.Upload>
        </Col>
      </Row>
    </>
  )
}

export default function ModalTicket(props: ModalTicketProps): JSX.Element {
  const { t } = useTranslation()
  const initialValues = getInitialValues(props.ticket)
  const [previewImage, setPreviewImage] = useState<string>()

  if (!props.ticket) {
    return <></>
  }

  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={props.handleSubmit}
        render={formikProps => (
          <Modal
            cancelText={t('common.actions.cancel')}
            closable
            destroyOnClose
            maskClosable
            okText={t('common.actions.save')}
            onCancel={() => props.toggle()}
            onOk={() => formikProps.handleSubmit()}
            title={t('web.receipt.form.title-edit', {
              number: props.ticket.request_ticket_number
            })}
            visible={Boolean(props.ticket)}
            width="55rem"
            wrapProps={{ 'data-test-id': 'receipt-modal' }}
          >
            <Form onSubmit={formikProps.handleSubmit}>
              <ModalTicketRender
                formikProps={formikProps}
                handleSubmit={props.handleSubmit}
                organization_id={props.organization_id}
                setPreviewImage={setPreviewImage}
                ticket={props.ticket}
              />
            </Form>
          </Modal>
        )}
        validationSchema={getValidationSchema({
          kmNeedJustification: isTenant559042084949(+props?.ticket?.tenant_id)
        })}
      />
      <ThumbnailPreview
        initPreview={previewImage}
        onCancel={() => setPreviewImage(null)}
      />
    </>
  )
}
